import React, { useEffect, useMemo, useRef, useState } from "react";
import Header from "@/components/Header";
import SearchHero from "@/components/SearchHero";
import MovieCard from "@/components/MovieCard";
import FilterBar from "@/components/FilterBar";
import NowInCinemas from "@/components/NowInCinemas";
import Top10Section from "@/components/Top10Section";
import { fetchCountries, fetchLanguages, fetchPlatforms, searchTitles, fetchPersonalized } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { useAuth } from "@/lib/auth";
import { Loader2, Sparkles, Clapperboard, Tv } from "lucide-react";

const DEFAULT_FILTERS = {
  platform: "", country: "", language: "",
  kind: "", year_min: "", year_max: "", sort: "popularity",
};

export default function Home() {
  const { t } = useLocale();
  const { user } = useAuth() || {};
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [personalized, setPersonalized] = useState(null);
  const [activeSection, setActiveSection] = useState("streaming");

  const streamingRef = useRef(null);
  const cinemasRef = useRef(null);

  useEffect(() => {
    Promise.all([fetchPlatforms(), fetchCountries(), fetchLanguages()])
      .then(([p, c, l]) => { setPlatforms(p); setCountries(c); setLanguages(l); });
  }, []);

  const runSearch = async (q = query) => {
    setLoading(true);
    try {
      const params = { q };
      if (filters.platform) params.platform = filters.platform;
      if (filters.country) params.country = filters.country;
      if (filters.language) params.language = filters.language;
      if (filters.kind) params.kind = filters.kind;
      if (filters.year_min) params.year_min = filters.year_min;
      if (filters.year_max) params.year_max = filters.year_max;
      if (filters.sort) params.sort = filters.sort;
      const data = await searchTitles(params);
      setResults(data.results);
    } finally { setLoading(false); }
  };

  useEffect(() => { runSearch(""); /* eslint-disable-next-line */ }, []);
  useEffect(() => { runSearch(query); /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    if (!user) { setPersonalized(null); return; }
    fetchPersonalized().then(setPersonalized).catch(() => {});
  }, [user]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActiveSection(visible.target.dataset.section);
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.2, 0.4] }
    );
    [streamingRef.current, cinemasRef.current].forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const platformsMap = useMemo(
    () => Object.fromEntries(platforms.map((p) => [p.id, p])), [platforms]
  );

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className={`min-h-screen relative section-${activeSection}`} data-testid="home-page">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 transition-opacity duration-[900ms] ease-out"
        style={{
          opacity: activeSection === "streaming" ? 1 : 0,
          backgroundImage:
            "radial-gradient(900px 600px at 12% -8%, rgba(245, 166, 35, 0.14), transparent 60%), " +
            "radial-gradient(700px 500px at 95% 0%, rgba(245, 166, 35, 0.06), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 transition-opacity duration-[900ms] ease-out"
        style={{
          opacity: activeSection === "cinemas" ? 1 : 0,
          backgroundImage:
            "radial-gradient(900px 600px at 12% -8%, rgba(220, 38, 38, 0.18), transparent 60%), " +
            "radial-gradient(700px 500px at 95% 0%, rgba(220, 38, 38, 0.08), transparent 60%)",
        }}
      />

      <Header />

      <div className="sticky top-[68px] z-30 flex justify-center pointer-events-none">
        <div className="pointer-events-auto glass-strong rounded-full p-1 flex items-center gap-1 border border-white/10 shadow-[0_20px_50px_-25px_rgba(0,0,0,0.9)]" data-testid="section-switcher">
          <button
            onClick={() => scrollTo(streamingRef)}
            className={`relative inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-heading uppercase tracking-[0.18em] transition-all duration-500 ${
              activeSection === "streaming" ? "bg-amber text-obsidian shadow-[0_8px_30px_-10px_rgba(245,166,35,0.7)]" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="section-streaming-tab"
          >
            <Tv className="h-3.5 w-3.5" /> Streaming
          </button>
          <button
            onClick={() => scrollTo(cinemasRef)}
            className={`relative inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-heading uppercase tracking-[0.18em] transition-all duration-500 ${
              activeSection === "cinemas" ? "bg-red-600 text-white shadow-[0_8px_30px_-10px_rgba(220,38,38,0.7)]" : "text-zinc-400 hover:text-white"
            }`}
            data-testid="section-cinemas-tab"
          >
            <Clapperboard className="h-3.5 w-3.5" /> {t("in_cinemas")}
          </button>
        </div>
      </div>

      {/* SECTION 1 — Streaming */}
      <section
        ref={streamingRef}
        data-section="streaming"
        className="pt-4 transition-colors duration-[900ms] ease-out"
        data-testid="streaming-section"
      >
        <SearchHero query={query} setQuery={setQuery} onSubmit={runSearch} />

        <Top10Section />

        {personalized && personalized.results?.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 pb-16" data-testid="personalized-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> {t("for_you")}
                </div>
                <h2 className="font-display text-3xl sm:text-4xl text-white">
                  {t("based_on")} {personalized.based_on?.length ? personalized.based_on.join(" · ") : "your library"}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 stagger">
              {personalized.results.slice(0, 12).map((r) => (
                <MovieCard key={r.id} item={r} platformsMap={platformsMap} compact />
              ))}
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-6 pb-24" data-testid="search-results-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2">The Atlas</div>
              <h2 className="font-display text-3xl sm:text-4xl text-white">
                {query ? `${t("results_for")} "${query}"` : t("browse_catalog")}
              </h2>
            </div>
            <div className="text-sm text-zinc-500 font-heading hidden sm:block">
              {results.length} {results.length === 1 ? "title" : "titles"}
            </div>
          </div>

          <FilterBar
            platforms={platforms} countries={countries} languages={languages}
            filters={filters} setFilters={setFilters}
          />

          {loading ? (
            <div className="flex items-center justify-center py-32 text-zinc-500">
              <Loader2 className="h-8 w-8 animate-spin text-amber" />
            </div>
          ) : results.length === 0 ? (
            <div className="py-24 text-center" data-testid="empty-state">
              <div className="font-display text-2xl text-white mb-2">{t("no_results_title")}</div>
              <p className="text-zinc-500 font-body">{t("no_results_sub")}</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 stagger" data-testid="results-grid">
              {results.map((r) => <MovieCard key={r.id} item={r} platformsMap={platformsMap} />)}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2 — Cinemas */}
      <section
        ref={cinemasRef}
        data-section="cinemas"
        className="pt-12 pb-16 transition-colors duration-[900ms] ease-out"
        data-testid="cinemas-section"
      >
        <NowInCinemas />
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-zinc-600 font-heading uppercase tracking-[0.25em]">
        MovioHub · Powered by Watchmode &amp; TMDB
      </footer>
    </div>
  );
}
